
import {DsAvatar, PhoneHQGlyph, PhoneHQAvatar} from 'ch3-ui-lib';
import {DirectionBadge} from '../Calls/callParts';
import {callRowView} from '../Calls/callRowView';
import {getFlag, getFormatedPhoneNumber} from '../../utilis/msisdnUtilis';
import {duration, getTimeAgo, DateFormatter, flatDurationInMinutes} from '../../utilis/timeUtilis';
import {useState} from "react";
import {ClickToCall} from "../../components/ClickToCall/ClickToCall";
import {FlagImage} from "react-international-phone";
import {useSelector} from "react-redux";
import {selectUserId} from "../../store/features/Auth/authSlice";
import {
    Call,
    useFetchCallSummaryQuery,
    useFetchCallTranscriptQuery,
    useLazyFetchCallRecordQuery,
    useRequestCallSummaryMutation,
    useRequestCallTranscriptionMutation,
} from "../../store/features/calls/callsService";
import PHQAudioPlayer from "../../components/AudioPlayer/PhoneHqAudioPlayer";
import {getFullName} from "../../utilis/getFullName";
import {AudioWaveform, Loader2, LockKeyhole, Sparkles} from 'lucide-react';
import {resolveAiInsightsState} from "../Calls/aiInsightsState.ts";
import {useInsightsDrawerParams} from "../Calls/insights/useInsightsDrawerParams";
import {isContentFiltered} from "../Calls/insights/insightsErrors";
import {insightsMessages} from "../Calls/insights/insightsMessages";
import {useIntl} from "react-intl";

interface RecentCallsProps {
    groupedCalls: { [key: string]: any };
}

type CallVariant = 'INCOMING' | 'OUTGOING' | 'MISSED';

const getCallVariant = (call: Call): CallVariant => {
    if (!call.bridgetAt && !call.forwardedTo) return 'MISSED';
    return call.type === 'INBOUND' ? 'INCOMING' : 'OUTGOING';
};

const getCallLabel = (variant: CallVariant, inbound: boolean): string => {
    switch (variant) {
        case 'INCOMING': return 'Incoming call';
        case 'OUTGOING': return 'Outgoing call';
        case 'MISSED': return inbound ? 'Missed incoming call' : 'Missed outgoing call';
    }
};

const RecentCallsDetails = ({groupedCalls}: RecentCallsProps) => {
    const [selectedCallId, setSelectedCallId] = useState<number | null>(null);
    const [fetchedRecordings, setFetchedRecordings] = useState<any>({});
    const [triggerFetch] = useLazyFetchCallRecordQuery();
    const currentUserId = useSelector(selectUserId);

    const handleCallClick = async (callId: number, recordId?: number) => {
        setSelectedCallId(callId === selectedCallId ? null : callId);

        if (recordId) {
            try {
                const {data: recordingUrl} = await triggerFetch(recordId);
                setFetchedRecordings((prevState: Record<number, string>) => ({
                    ...prevState,
                    [recordId]: recordingUrl,
                }));
            } catch (error) {
                console.error('Failed to fetch recording:', error);
            }
        }
    };

    if (!groupedCalls) return null;
    const sortedGroups = Object.keys(groupedCalls).sort((a, b) => new Date(b).getTime() - new Date(a).getTime());

    return (
        <div className="w-full b">
            {sortedGroups.map(date => {
                if (!groupedCalls[date].length) return null;
                return (
                    <div key={date}>
                        {groupedCalls[date].map((call: Call) => {
                            const variant = getCallVariant(call);
                            const inbound = call.type === 'INBOUND';
                            const isSelected = selectedCallId === call.callId;
                            const handlerName = call.user
                                ? (String(call.user.userId) === String(currentUserId)
                                    ? 'You'
                                    : getFullName(call.user.firstName, call.user.lastName))
                                : null;

                            return (
                                <div
                                    key={call.callId}
                                    className={`mx-4 my-0.5 rounded-[12px] border border-solid ${isSelected ? 'border-brand-disabled' : 'border-transparent'}`}
                                >
                                    {/* Collapsed row */}
                                    <div
                                        role="button"
                                        tabIndex={0}
                                        aria-expanded={isSelected}
                                        onClick={() => handleCallClick(call.callId, call?.recordingId)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                handleCallClick(call.callId, call?.recordingId);
                                            }
                                        }}
                                        className={`flex min-h-[52px] cursor-pointer items-center gap-2 rounded-control px-2 outline-none focus-visible:ring-2 focus-visible:ring-brand-focus ${isSelected ? '' : 'hover:bg-surface-muted'}`}
                                    >
                                        <span className="flex min-w-0 flex-col items-start gap-1">
                                            <span className="truncate text-callout font-semibold text-ink">
                                                {call.clientMsisdn ? getFormatedPhoneNumber(call.clientMsisdn, true) : getCallLabel(variant, inbound)}
                                            </span>
                                            <DirectionBadge view={callRowView(call)}/>
                                        </span>

                                        {call.recordingId && (
                                            <AudioWaveform size={16} aria-label="Recorded" className="!p-0 shrink-0 text-brand-ink"/>
                                        )}

                                        <span className="flex-1"/>

                                        {handlerName && (
                                            <span className="inline-flex items-center gap-1.5 text-footnote font-medium text-ink">
                                                <DsAvatar size={20} name={call.user ? getFullName(call.user.firstName, call.user.lastName) : ''} src={call.user?.avatarUrl || undefined}/>
                                                {handlerName}
                                            </span>
                                        )}

                                        <span className={`w-[60px] whitespace-nowrap text-right text-footnote ${variant === 'MISSED' && inbound ? 'text-danger-ink' : 'text-ink-muted-strong'}`}>
                                            {getTimeAgo(new Date(call.startedAt))}
                                        </span>

                                        {Boolean(call.exposedMsisdn) && (
                                            <span
                                                role="img"
                                                aria-label={`Line: ${call.exposedMsisdn.label || getFormatedPhoneNumber(call.exposedMsisdn.msisdn, true)}`}
                                                title={call.exposedMsisdn.label}
                                                className="flex size-6 shrink-0 items-center justify-center rounded-badge bg-surface-muted text-ink-secondary"
                                            >
                                                <PhoneHQGlyph name={call.exposedMsisdn.icon || 'call'} size={12}/>
                                            </span>
                                        )}
                                    </div>

                                    {/* Expanded card */}
                                    {isSelected && (
                                        <ExpandedCallCard
                                            call={call}
                                            variant={variant}
                                            handlerName={handlerName}
                                            fetchedRecordings={fetchedRecordings}
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                );
            })}
        </div>
    );
};

interface ExpandedCallCardProps {
    call: Call;
    variant: CallVariant;
    handlerName: string | null;
    fetchedRecordings: {[key: number]: string};
}

const ExpandedCallCard = ({call, variant, handlerName, fetchedRecordings}: ExpandedCallCardProps) => {
    const {
        data: transcript,
        isFetching: isFetchingTranscript,
        error: transcriptError,
    } = useFetchCallTranscriptQuery(call.callId, {skip: !call.callId});

    const {
        data: summary,
        isFetching: isFetchingSummary,
        error: summaryError,
    } = useFetchCallSummaryQuery(call.callId, {skip: !call.callId});

    const [generateSummaryMutation, {isLoading: isGeneratingSummary}] = useRequestCallSummaryMutation();
    const [generateTranscriptMutation, {isLoading: isGeneratingTranscript}] = useRequestCallTranscriptionMutation();
    const insightsDrawer = useInsightsDrawerParams();
    const intl = useIntl();
    const [aiError, setAiError] = useState<unknown>(null);

    const hasTranscript = !!transcript && !transcriptError;
    const hasSummary = !!summary && !summaryError;
    const aiInsightsState = resolveAiInsightsState({
        hasTranscript,
        hasSummary,
        hasRecording: Boolean(call.recordingId),
    });
    const isGenerating = isGeneratingSummary || isGeneratingTranscript;
    const isJustFetching = (isFetchingTranscript || isFetchingSummary) && !isGenerating;
    const isCallWithTranscript = Boolean(
        flatDurationInMinutes(call?.bridgetAt, call?.finishedAt) < 15 ||
        Boolean(flatDurationInMinutes(call?.forwardedAt, call?.finishedAt) < 15) ||
        (call?.hasVoicemail && Boolean(flatDurationInMinutes(call?.startedAt, call?.finishedAt) < 15))
    );

    const generateMissingAiArtifact = async () => {
        if (!call.callId || !aiInsightsState.action) return;
        setAiError(null);
        try {
            if (aiInsightsState.action.request === "transcript") {
                await generateTranscriptMutation({callId: call.callId}).unwrap();
            } else {
                await generateSummaryMutation({callId: call.callId}).unwrap();
            }
        } catch (error) {
            setAiError(error);
        }
    };

    const showCallLength = variant !== 'MISSED' && call.bridgetAt;
    const showHandler = variant !== 'MISSED' && handlerName;
    const handlerLabel = variant === 'OUTGOING' ? 'Caller' : 'Handled by';
    const recordingUrl = call.recordingId ? fetchedRecordings[call.recordingId] : undefined;
    const showRecordingPanel = Boolean(call.recordingId);

    const renderAiAction = () => {
        if (isGenerating) {
            return (
                <span className="inline-flex items-center gap-2 bg-surface border border-line rounded-lg px-3 py-1.5 text-footnote font-medium text-ink-secondary">
                    <Loader2 size={14} className="animate-spin"/>
                    Generating AI Insights...
                </span>
            );
        }
        if (isJustFetching) {
            return (
                <span className="inline-flex items-center gap-2 bg-surface border border-line rounded-lg px-3 py-1.5 text-footnote font-medium text-ink-secondary">
                    <Loader2 size={14} className="animate-spin"/>
                </span>
            );
        }
        // Anything already generated is worth opening, even a summary on its own.
        if (aiInsightsState.canView) {
            return (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        insightsDrawer.open(call.callId, {entry: 'contact_history', returnFocus: e.currentTarget});
                    }}
                    className="inline-flex items-center gap-2 bg-surface border border-line rounded-lg px-3 py-1.5 text-footnote font-medium text-brand-ink cursor-pointer hover:bg-canvas"
                >
                    <Sparkles size={14} className="text-brand-ink"/>
                    View AI Insights
                </button>
            );
        }
        if (isContentFiltered(aiError)) {
            return (
                <span role="alert" className="max-w-[60%] text-right text-footnote font-medium text-danger-ink">
                    {intl.formatMessage(insightsMessages.contentFiltered)}
                </span>
            );
        }
        if (aiInsightsState.unavailableReason) {
            return (
                <span className="text-footnote font-medium text-ink-muted">
                    {aiInsightsState.unavailableReason}
                </span>
            );
        }
        if (aiInsightsState.action && isCallWithTranscript) {
            return (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        generateMissingAiArtifact();
                    }}
                    className="inline-flex items-center gap-2 bg-surface border border-line rounded-lg px-3 py-1.5 text-footnote font-medium text-brand-ink cursor-pointer hover:bg-canvas"
                >
                    <Sparkles size={14} className="text-brand-ink"/>
                    {aiInsightsState.action.label}
                </button>
            );
        }
        return null;
    };

    return (
        <div className="flex flex-col">
            {/* Recording panel (header + audio player) */}
            {showRecordingPanel && (
                <div className=" mb-3  border border-indigo-100 overflow-hidden">
                    <div className="flex items-center justify-between bg-brand-tint-subtle/60 px-3 py-3">
                        <div className="flex items-center gap-3">
                            <AudioWaveform size={16} className="text-brand-ink"/>
                            <span className="text-footnote font-medium text-ink">
                                {call.hasVoicemail ? 'Voicemail' : 'Recording'}
                            </span>
                        </div>
                        {renderAiAction()}
                    </div>
                    {recordingUrl && (
                        <div className="bg-surface px-3 py-2 bg-brand-tint-subtle/60">
                            <PHQAudioPlayer
                                src={recordingUrl}
                                className="w-full"
                                filename={`${(call.bridgetAt || '').replace(/[.:]/g, '-')}_${call.clientMsisdn}`}
                            />
                        </div>
                    )}
                </div>
            )}

            {/* Date */}
            <DataRow label="Date">
                {Boolean(call.startedAt) && (
                    <time dateTime={call.startedAt}>
                        {DateFormatter(call.startedAt)}
                    </time>
                )}
            </DataRow>

            {/* Call length */}
            {showCallLength && (
                <DataRow label="Call length">
                    {duration(call.bridgetAt, call.finishedAt)}
                </DataRow>
            )}

            {/* Handler (incoming → Handled by, outgoing → Caller) */}
            {showHandler && (
                <DataRow label={handlerLabel}>
                    <div className="flex items-center gap-2">
                        <PhoneHQAvatar
                            size="20px"
                            type={call.user?.avatarUrl ? "image" : "initials"}
                            src={call.user?.avatarUrl}
                            name={call.user ? getFullName(call.user.firstName, call.user.lastName) : (handlerName ?? '')}
                            colorFromName
                        />
                        <span>{handlerName}</span>
                    </div>
                </DataRow>
            )}

            {/* Phone number */}
            {Boolean(call.clientMsisdn) && (
                <DataRow label="Phone number">
                    <div className="flex items-center gap-2">
                        <FlagImage className="w-4 h-4" iso2={getFlag(call.clientMsisdn) ?? 'us'}/>
                        {call.exposedMsisdn?.label && (
                            <span className="text-ink-muted">{call.exposedMsisdn.label}</span>
                        )}
                        <ClickToCall noPrefix={true} msisdn={call.clientMsisdn}/>
                    </div>
                </DataRow>
            )}

            {/* Internal badge as inline (if applicable) */}
            {(call as any).contactType?.type === 'INTERNAL' && (
                <div className="px-3 py-2 border-b border-surface-muted">
                    <span className="inline-flex items-center gap-1 border border-indigo-200 text-brand-ink rounded-md px-2 py-0.5 text-[11px] font-medium">
                        <LockKeyhole size={12}/>
                        Internal
                    </span>
                </div>
            )}

        </div>
    );
};

const DataRow = ({label, children}: {label: string; children: React.ReactNode}) => (
    <div className="flex items-center px-3 py-3 border-b border-surface-muted last:border-b-0">
        <div className="w-[150px] text-footnote font-medium text-ink-muted">{label}</div>
        <div className="flex-1 text-footnote font-medium text-ink">{children}</div>
    </div>
);

export default RecentCallsDetails;
